// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionReportFooter.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import action from './Izenda.PermissionReportFooterActions';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import { isNil } from 'lodash/fp';

const PermissionReportFooter = props => {
  const { permission, permissionStore, localization, authorization, readOnly, isTemplatePermissions } = props;
  const reports = permission.reports;
  const tenantDetail = permissionStore.tenantDetail;
  const hasReportDesignerLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportDesigner);
  const hasScheduleLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Scheduling);
  const hasAlertingLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Alerting);
  const hasExportLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);

  const renderActionsContent = () => {
    const actionsContent = [];

    // row 1 includes label action, schedule, subscribe, configure access rights
    actionsContent.push(
      <GridLayout.Row className='u_paddingTop u_paddingBottom' key='actionsContent_0'>
        <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
          {localization.getText('COMMON_LABEL_ACTIONS')}
        </GridLayout.Col>
        {hasScheduleLicense &&
          hasReportDesignerLicense &&
          (isNil(tenantDetail) || tenantDetail.reports.actions.schedule) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.schedule)}
                    onChange={props.permissionUpdateReportActionSchedule(reports.actions)}
                  />
                  <span>{localization.getText('COMMON_LABEL_SCHEDULE')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
        {hasAlertingLicense &&
          hasScheduleLicense &&
          (isNil(tenantDetail) || tenantDetail.reports.actions.registerForAlerts) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions || !props.getCheckedCommon(reports.actions.schedule)}
                    checked={props.getCheckedCommon(reports.actions.registerForAlerts)}
                    onChange={props.permissionUpdateData(reports.actions, 'registerForAlerts')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_REGISTER_TO_ALERTS')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
        {hasScheduleLicense && (isNil(tenantDetail) || tenantDetail.reports.actions.subscribe) && (
          <GridLayout.Col cols={2}>
            <div className='u_inlineBlock izenda-PermissionCategory-group'>
              <label>
                <Input
                  type='checkbox'
                  readOnly={readOnly}
                  disabled={isTemplatePermissions}
                  checked={props.getCheckedCommon(reports.actions.subscribe)}
                  onChange={props.permissionUpdateData(reports.actions, 'subscribe')}
                />
                <span>{localization.getText('COMMON_LABEL_SUBSCRIBE')}</span>
              </label>
            </div>
          </GridLayout.Col>
        )}
        {hasReportDesignerLicense && (isNil(tenantDetail) || tenantDetail.reports.actions.configureAccessRights) && (
          <GridLayout.Col cols={3}>
            <div className='u_inlineBlock izenda-PermissionCategory-group'>
              <label>
                <Input
                  type='checkbox'
                  readOnly={readOnly}
                  disabled={isTemplatePermissions}
                  checked={props.getCheckedCommon(reports.actions.configureAccessRights)}
                  onChange={props.permissionUpdateData(reports.actions, 'configureAccessRights')}
                />
                <span>{localization.getText('PERMISSION_LABEL_CONFIGURE_ACCESS_RIGHT')}</span>
              </label>
            </div>
          </GridLayout.Col>
        )}
        {permission.isTenantSetup && (
          <GridLayout.Col cols={1} className='u_textCenter u_floatRight'>
            <Input
              type='checkbox'
              readOnly={readOnly}
              disabled={isTemplatePermissions}
              checked={props.getCheckedCommon(reports.actions.tenantAccess)}
              onChange={props.permissionUpdateTenantAccess(reports, reports.actions)}
            />
          </GridLayout.Col>
        )}
      </GridLayout.Row>
    );

    // row 2 includes email, print, exporting
    if (
      isNil(tenantDetail) ||
      tenantDetail.reports.actions.email ||
      tenantDetail.reports.actions.print ||
      tenantDetail.reports.actions.exporting
    ) {
      actionsContent.push(
        <GridLayout.Row className='u_paddingTop u_paddingBottom' key='actionsContent_1'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory' />
          {(isNil(tenantDetail) || tenantDetail.reports.actions.email) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.email)}
                    onChange={props.permissionUpdateData(reports.actions, 'email')}
                  />
                  <span>{localization.getText('COMMON_LABEL_EMAIL')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          {hasExportLicense && (isNil(tenantDetail) || tenantDetail.reports.actions.print) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.print)}
                    onChange={props.permissionUpdateData(reports.actions, 'print')}
                  />
                  <span>{localization.getText('COMMON_LABEL_PRINT')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          {hasExportLicense && (isNil(tenantDetail) || tenantDetail.reports.actions.exporting) && (
            <GridLayout.Col cols={5}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.exporting)}
                    onChange={props.permissionUpdateData(reports.actions, 'exporting')}
                  />
                  <span>{localization.getText('COMMON_LABEL_EXPORT')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          <GridLayout.Col cols={1} />
        </GridLayout.Row>
      );
    }

    // row 3 includes View Report History, Unarchive Report Versions
    if (
      isNil(tenantDetail) ||
      tenantDetail.reports.actions.viewReportHistory ||
      tenantDetail.reports.actions.unarchiveReportVersions
    ) {
      actionsContent.push(
        <GridLayout.Row className='u_paddingTop u_paddingBottom' key='actionsContent_2'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory' />
          {(isNil(tenantDetail) || tenantDetail.reports.actions.viewReportHistory) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.viewReportHistory)}
                    onChange={props.permissionUpdateData(reports.actions, 'viewReportHistory')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_VIEW_REPORT_HISTORY')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          {(isNil(tenantDetail) || tenantDetail.reports.actions.unarchiveReportVersions) && (
            <GridLayout.Col cols={7}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.unarchiveReportVersions)}
                    onChange={props.permissionUpdateData(reports.actions, 'unarchiveReportVersions')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_UNARCHIVE_REPORT_VERSIONS')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          <GridLayout.Col cols={1} />
        </GridLayout.Row>
      );
    }

    // row 4 includes del, overwrite existing report
    if (
      isNil(tenantDetail) ||
      tenantDetail.reports.actions.del ||
      tenantDetail.reports.actions.overwriteExistingReport
    ) {
      actionsContent.push(
        <GridLayout.Row className='u_paddingTop u_paddingBottom' key='actionsContent_3'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory' />
          {(isNil(tenantDetail) || tenantDetail.reports.actions.del) && (
            <GridLayout.Col cols={2}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.del)}
                    onChange={props.permissionUpdateData(reports.actions, 'del')}
                  />
                  <span>{localization.getText('COMMON_LABEL_DELETE')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          {(isNil(tenantDetail) || tenantDetail.reports.actions.overwriteExistingReport) && (
            <GridLayout.Col cols={7}>
              <div className='u_inlineBlock izenda-PermissionCategory-group'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.actions.overwriteExistingReport)}
                    onChange={props.permissionUpdateData(reports.actions, 'overwriteExistingReport')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_OVERWRITE_EXISTING_REPORT')}</span>
                </label>
              </div>
            </GridLayout.Col>
          )}
          <GridLayout.Col cols={1} />
        </GridLayout.Row>
      );
    }
    return actionsContent;
  };

  return (
    <GridLayout className='izenda-GridLayout-Custom'>
      {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.reports.filterProperties)) && (
        <GridLayout.Row className='u_paddingTop u_paddingBottom'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
            {localization.getText('COMMON_LABEL_FILTER_PROPERTIES')}
          </GridLayout.Col>
          <GridLayout.Col cols={9}>
            {(isNil(tenantDetail) || tenantDetail.reports.filterProperties.filterLogic) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.filterProperties.filterLogic)}
                    onChange={props.permissionUpdateData(reports.filterProperties, 'filterLogic')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_FILTER_LOGIC')}</span>
                </label>
              </div>
            )}
            {(isNil(tenantDetail) || tenantDetail.reports.filterProperties.crossFiltering) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(reports.filterProperties.crossFiltering)}
                    onChange={props.permissionUpdateData(reports.filterProperties, 'crossFiltering')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_CROSS_FILTERING')}</span>
                </label>
              </div>
            )}
          </GridLayout.Col>
          {permission.isTenantSetup && (
            <GridLayout.Col cols={1} className='u_textCenter'>
              <Input
                type='checkbox'
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                checked={props.getCheckedCommon(reports.filterProperties.tenantAccess)}
                onChange={props.permissionUpdateTenantAccess(reports, reports.filterProperties)}
              />
            </GridLayout.Col>
          )}
        </GridLayout.Row>
      )}
      {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.reports.fieldProperties)) && (
        <GridLayout.Row className='u_paddingTop u_paddingBottom'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
            {localization.getText('COMMON_LABEL_FIELD_PROPERTIES')}
          </GridLayout.Col>
          <GridLayout.Col cols={9}>
            {(isNil(tenantDetail) || tenantDetail.reports.fieldProperties.customURL) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    name='fieldProperties'
                    checked={props.getCheckedCommon(reports.fieldProperties.customURL)}
                    onChange={props.permissionUpdateData(reports.fieldProperties, 'customURL')}
                  />
                  <span>{localization.getText('COMMON_LABEL_CUSTOM_URL')}</span>
                </label>
              </div>
            )}
            {(isNil(tenantDetail) || tenantDetail.reports.fieldProperties.embeddedJavaScript) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    name='fieldProperties'
                    checked={props.getCheckedCommon(reports.fieldProperties.embeddedJavaScript)}
                    onChange={props.permissionUpdateData(reports.fieldProperties, 'embeddedJavaScript')}
                  />
                  <span>{localization.getText('COMMON_LABEL_EMBEDDED_JAVASCRIPT')}</span>
                </label>
              </div>
            )}
            {(isNil(tenantDetail) || tenantDetail.reports.fieldProperties.subreport) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    name='fieldProperties'
                    checked={props.getCheckedCommon(reports.fieldProperties.subreport)}
                    onChange={props.permissionUpdateData(reports.fieldProperties, 'subreport')}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_SUBREPORT')}</span>
                </label>
              </div>
            )}
          </GridLayout.Col>
          {permission.isTenantSetup && (
            <GridLayout.Col cols={1} className='u_textCenter'>
              <Input
                type='checkbox'
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                checked={props.getCheckedCommon(reports.fieldProperties.tenantAccess)}
                onChange={props.permissionUpdateTenantAccess(reports, reports.fieldProperties)}
              />
            </GridLayout.Col>
          )}
        </GridLayout.Row>
      )}
      {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.reports.actions)) && renderActionsContent()}
    </GridLayout>
  );
};

PermissionReportFooter.propTypes = {
  readOnly: PropTypes.bool,
  authorization: PropTypes.object,
  localization: PropTypes.object,
  tenantDetail: PropTypes.object,
  permissionStore: PropTypes.object,
  permission: PropTypes.object.isRequired,
  checkShowGroupCommon: PropTypes.func,
  getCheckedCommon: PropTypes.func
};

export default connect(action)(PermissionReportFooter);



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/PermissionBody/Izenda.PermissionReportFooter.js